import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, StripeElementLocale } from '@stripe/stripe-js'
import { User } from '@supabase/supabase-js'
import { useTranslation } from 'react-i18next'
import { Navigate, useLoaderData, useSearchParams } from 'react-router-dom'
import CheckoutForm from '../../components/CheckoutForm'
import PageLayout from '../../components/PageLayout'
import { GetPricesData } from '../../loaders/getPricesLoader'
import { Plan } from '../../types'

// Locales Stripe Elements can localize placeholders and validation messages in.
// Kept in sync with the StripeElementLocale union from @stripe/stripe-js.
const stripeSupportedLocales = new Set<StripeElementLocale>([
  'ar',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'es',
  'et',
  'fi',
  'fil',
  'fr',
  'he',
  'hr',
  'hu',
  'id',
  'it',
  'ja',
  'ko',
  'lt',
  'lv',
  'ms',
  'mt',
  'nb',
  'nl',
  'no',
  'pl',
  'pt',
  'ro',
  'ru',
  'sk',
  'sl',
  'sv',
  'th',
  'tr',
  'vi',
  'zh',
])

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string,
)

/**
 * Resolves a CSS variable to its computed value.
 * Stripe Elements doesn't support CSS variables, so we need to resolve them at runtime.
 */
function getCssVariableValue(variableName: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim()
}

export default function PaymentPage() {
  const [searchParams] = useSearchParams()
  const [pricesData, user] = useLoaderData() as [GetPricesData, User]

  const clientSecret = searchParams.get('client_secret')!
  const currency = searchParams.get('currency')!
  const intentMode =
    searchParams.get('intent_mode') === 'setup' ? 'setup' : 'payment'
  const plan = searchParams.get('plan')! as Plan
  const subscriptionId = searchParams.get('subscription_id') ?? ''
  const trialPeriodDaysParam = searchParams.get('trial_period_days')
  const trialPeriodDays = trialPeriodDaysParam
    ? Number.parseInt(trialPeriodDaysParam, 10)
    : undefined

  const { i18n, t } = useTranslation()

  const stripeLocale: StripeElementLocale = stripeSupportedLocales.has(
    i18n.language as StripeElementLocale,
  )
    ? (i18n.language as StripeElementLocale)
    : 'auto'

  if (!clientSecret) return <Navigate replace to="/checkout/plans" />

  return (
    <PageLayout>
      <div className="flex grow flex-col">
        <div className="shrink-0 space-y-2">
          <p className="font-bold">
            {t('pages.checkout.payment.securePaymentTitle')}
          </p>

          <p>{t('pages.checkout.payment.securePaymentText')}</p>

          <p className="font-bold">
            {t('pages.checkout.payment.moneyBackGuaranteeTitle')}
          </p>

          <p>{t('pages.checkout.payment.moneyBackGuaranteeText')}</p>
        </div>

        <div className="mt-4 grow">
          <Elements
            options={{
              appearance: {
                labels: 'floating',
                theme: 'flat',
                variables: {
                  colorPrimary: getCssVariableValue('--color-primary'),
                  // colorBackground: getCssVariableValue('--color-app-bg'),
                  borderRadius: '20px',
                  fontFamily: getCssVariableValue('--font-sans'),
                },
              },
              clientSecret,
              locale: stripeLocale,
            }}
            stripe={stripePromise}
          >
            <CheckoutForm
              clientSecret={clientSecret}
              currency={currency}
              intentMode={intentMode}
              plan={plan}
              pricesData={pricesData}
              subscriptionId={subscriptionId}
              trialPeriodDays={trialPeriodDays}
              userEmail={user?.user_metadata?.original_email ?? ''}
            />
          </Elements>
        </div>
      </div>
    </PageLayout>
  )
}
